import { BRICK_H, STUD } from "./LegoBrick";

/**
 * LEGO clutch without a physics engine.
 * Bricks live on integer studs. A piece only locks if every cell is empty
 * and at least one stud underneath is solid (pedestal or another brick).
 */
export type SnapPose = {
  sx: number;
  sz: number;
  w: number;
  d: number;
  layer: number;
  x: number;
  y: number;
  z: number;
};

function key(sx: number, sz: number, layer: number) {
  return `${sx},${sz},${layer}`;
}

export class StudGrid {
  private filled = new Set<string>();

  clear() {
    this.filled.clear();
  }

  occupy(pose: SnapPose) {
    for (const [sx, sz] of cells(pose.sx, pose.sz, pose.w, pose.d)) {
      this.filled.add(key(sx, sz, pose.layer));
    }
  }

  worldOf(sx: number, sz: number, w: number, d: number, layer: number, floorY: number): SnapPose {
    return {
      sx,
      sz,
      w,
      d,
      layer,
      x: (sx + (w - 1) / 2) * STUD,
      z: (sz + (d - 1) / 2) * STUD,
      y: floorY + layer * BRICK_H + BRICK_H / 2,
    };
  }

  /** Snap a world aim-point to the lowest legal clutch. */
  snap(w: number, d: number, worldX: number, worldZ: number, floorY: number): SnapPose | null {
    const ox = Math.round(worldX / STUD - (w - 1) / 2);
    const oz = Math.round(worldZ / STUD - (d - 1) / 2);
    let best: SnapPose | null = null;
    let bestDist = Infinity;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const pose = this.fit(w, d, ox + dx, oz + dz, floorY);
        if (!pose) continue;
        const dist = (pose.x - worldX) ** 2 + (pose.z - worldZ) ** 2 + pose.layer * 0.01;
        if (dist < bestDist) {
          bestDist = dist;
          best = pose;
        }
      }
    }
    return best;
  }

  private fit(w: number, d: number, sx: number, sz: number, floorY: number): SnapPose | null {
    for (let layer = 0; layer < 16; layer++) {
      if (this.blocked(sx, sz, w, d, layer)) continue;
      if (!this.supported(sx, sz, w, d, layer)) continue;
      return this.worldOf(sx, sz, w, d, layer, floorY);
    }
    return null;
  }

  private blocked(sx: number, sz: number, w: number, d: number, layer: number): boolean {
    for (const [x, z] of cells(sx, sz, w, d)) {
      if (this.filled.has(key(x, z, layer))) return true;
    }
    return false;
  }

  private supported(sx: number, sz: number, w: number, d: number, layer: number): boolean {
    if (layer === 0) {
      for (const [x, z] of cells(sx, sz, w, d)) {
        if (Math.hypot(x * STUD, z * STUD) > 3.2) return false;
      }
      return true;
    }
    let n = 0;
    for (const [x, z] of cells(sx, sz, w, d)) {
      if (this.filled.has(key(x, z, layer - 1))) n++;
    }
    return n > 0;
  }
}

function* cells(sx: number, sz: number, w: number, d: number): Generator<[number, number]> {
  for (let x = sx; x < sx + w; x++) {
    for (let z = sz; z < sz + d; z++) {
      yield [x, z];
    }
  }
}
